import {GeneralUtility} from '../utilities/general-utility';
import {LanguageUtility} from '../utilities/language-utility';

/**
 * Contains helper function for managing forms.
 *
 * @author Philipp Seiler <sep@earlybird.at>
 */
export class FormHelpers {
  // Contains changed values of all forms, that should trigger an alert-message when the user tries to exit the page with unsaved data.
  private static changedForms: { [key: string]: any } = {};

  /**
   * Adds event listeners to all forms, that check if a form has changed any of its values or not. Will be used by FormHelpers::checkIfFormsChanged to show
   * an alert-message to the user, if a form has been edited, and the page is being exited.
   */
  public static initializeUnsavedFormsCheck(): void {
    const forms = document.querySelectorAll('form[data-unsaved-check="1"]');
    for (const form of forms) {
      const formName = form.getAttribute('name');
      if (!formName) {
        return;
      }

      // Set up an object storage for each form, to accumulate changed input values
      const changedFields: { [key: string]: any } = {};

      const fields = form.querySelectorAll<HTMLInputElement>('input:not([data-ignore]),select:not([data-ignore]),textarea:not([data-ignore])');
      for (const field of fields) {
        const fieldName = field.getAttribute('name');
        if (!fieldName) {
          return;
        }

        const originalValue = field.value;

        // Each input element is initialized with "no changes present (false)". The input-event-listener will set this to "has changed (true)", if
        // applicable.
        changedFields[fieldName] = false;
        field.addEventListener('input', () => {
          // Value differs from the original value => consider input as changed
          if (originalValue !== field.value) {
            changedFields[fieldName] = true;
          }
          // Input has already been marked as changed, but the value has been reverted to the original value => consider as not changed anymore
          else if (changedFields[fieldName] && originalValue === field.value) {
            changedFields[fieldName] = false;
          }
        });
      }

      FormHelpers.changedForms[formName] = changedFields;

      // Unset any has-changed values, if the form has been submitted
      form.addEventListener('submit', () => {
        delete FormHelpers.changedForms[formName];
      });
    }
  }

  /**
   * Looks through the changed values as created by the event listeners of FormHelpers::initializeUnsavedFormsCheck.
   *
   * @returns {boolean} True when there is at least one changed input element, otherwise false
   */
  public static checkIfUnsavedFormsPresent(): boolean {
    for (const formName in FormHelpers.changedForms) {
      for (const changedInputElementName in FormHelpers.changedForms[formName]) {
        const hasChanged = FormHelpers.changedForms[formName][changedInputElementName];
        if (hasChanged) {
          return true;
        }
      }
    }
    return false;
  }

  constructor() {
    this.initializeScrollToForms();
    this.initializeTextareas();
    this.initializeSanitization();
  }

  /**
   * Initializes automatic scrolling to sent forms. A form needs to set a URL hash in the form "form-[formId]" for this to work.
   */
  private initializeScrollToForms(): void {
    const hash = window.location.hash;
    if (!hash || hash.indexOf('#form-') === -1) {
      return;
    }

    const formId = hash.replace('#form-', '');
    const form = document.querySelector<HTMLFormElement>('form#' + formId);
    if (form) {
      const position = GeneralUtility.getElementPosition(form);
      window.scrollTo({
        top: position.top > 0 ? position.top - 250 : 0,
        behavior: 'smooth'
      });
    }
  }

  /**
   * Initializes all textareas with custom functions.
   */
  private initializeTextareas(): void {
    // Show allowed characters left underneath a textarea
    const label = LanguageUtility.translate('characters');
    const textareas = document.querySelectorAll<HTMLTextAreaElement>('textarea[maxlength]');
    for (const textarea of textareas) {
      let maxLength = 0;
      const maxLengthAttribute = textarea.getAttribute('maxlength');
      if (maxLengthAttribute) {
        maxLength = parseInt(maxLengthAttribute);
      }

      if (maxLength > 0) {
        const info = document.createElement('span');
        info.classList.add('form-text');

        const remaining = document.createElement('span');
        remaining.classList.add('remaining');
        remaining.textContent = maxLength.toString();

        info.append(remaining);
        info.insertAdjacentText('beforeend', ' / ' + maxLength + ' ' + label);
        textarea.insertAdjacentElement('afterend', info);

        textarea.addEventListener('keyup', () => {
          const remainingCharacters = maxLength - textarea.value.length;
          remaining.textContent = remainingCharacters.toString();
        });
      }
    }
  }

  /**
   * Initializes sanitizing of form values.
   */
  private initializeSanitization(): void {
    const sanitizeFloat = function (value: string): string {
      return value.replace(',', '.');
    };

    const fields = document.querySelectorAll<HTMLInputElement>('[data-sanitize]');
    for (const field of fields) {
      if (!field.dataset.sanitize) {
        return;
      }

      field.addEventListener('blur', () => {
        if (field.value) {
          const value = field.value.trim();
          switch (field.dataset.sanitize) {
            case 'float':
              field.value = sanitizeFloat(value);
              break;
          }
        }
      });
    }
  }
}
